// Copyright (c) 2022 by Move Closer

import { ImageData, IRelatedService, ModuleContentResolver } from '../../contexts'

import { logger } from '../../support'
import { toImageProps } from '../../front/shared/support'

import { ArtisticCarouselProps } from '../../dsl/molecules/ArtisticCarousel'
import { Level } from '../../dsl/atoms/Heading'
import { SizeMap } from '../../dsl/composables'

import { resolveImage, resolveLink } from '../resolvers'
import { ResolvedLink } from '../partials'

import {
  ArtisticCarouselItem,
  ArtisticCarouselModuleContent,
  ArtisticCarouselModuleContentInput,
  ArtisticCarouselModuleData
} from './ArtisticCarousel.contracts'

/**
 * @author Maciej Perzankowski <maciej.perzankowski@movecloser.pl>
 */
export const resolveArtisticCarouselModuleContent: ModuleContentResolver<ArtisticCarouselModuleContentInput, ArtisticCarouselModuleContent> =
  async (
    input: ArtisticCarouselModuleContentInput,
    relatedService: IRelatedService
  ): Promise<ArtisticCarouselModuleContent> => {
    let resolvedImage: ImageData | null = null
    let resolvedLink: ResolvedLink | null = null
    let slides: ArtisticCarouselItem[] = []
    const {
      image,
      link,
      slides: inputSlides
    } = input

    if (image) {
      resolvedImage = await resolveImage(image, relatedService)
    }

    if (link) {
      if (link.relatedTarget) {
        resolvedLink = await resolveLink(link, relatedService)
      } else {
        resolvedLink = link as ResolvedLink
      }
    }

    try {
      slides = await Promise.all(inputSlides.map(async ({
        image: slideImage,
        link: slideLink,
        ...rest
      }) => {
        let resolvedBackground: ImageData | null = null
        let resolvedLink: ResolvedLink | null = null

        if (slideImage) {
          resolvedBackground = await resolveImage(slideImage, relatedService)
        }

        if (slideLink) {
          if (slideLink.relatedTarget) {
            resolvedLink = await resolveLink(slideLink, relatedService)
          } else {
            resolvedLink = slideLink as ResolvedLink
          }
        }

        return {
          image: resolvedBackground,
          link: resolvedLink,
          ...rest
        }
      }))
    } catch (e) {
      logger(e, 'warn')
    }

    return {
      ...input,
      slides,
      image: resolvedImage,
      link: resolvedLink
    }
  }

export const translateContentToArtisticCarouselProps = (content: ArtisticCarouselModuleData): ArtisticCarouselProps => {
  const {
    heading,
    image,
    link,
    slides
  } = content

  return {
    heading: {
      ...heading,
      wrappers: [],
      level: heading?.level as Level,
      size: heading?.size ?? SizeMap.Medium
    },
    link: {
      ...link,
      target: link?.target ?? '',
      label: link?.label ?? ''
    },
    image: image ? toImageProps(image) : {
      src: '',
      alt: ''
    },
    slides: slides.map((slide) => {
      return {
        ...slide,
        background: slide.image ? toImageProps(slide.image) : {
          src: '',
          alt: ''
        },
        heading: {
          ...slide.heading,
          wrappers: [],
          level: slide.heading?.level as Level,
          size: slide.heading?.size ?? SizeMap.Medium
        },
        link: {
          ...slide.link,
          target: slide.link?.target ?? '',
          label: slide.link?.label ?? ''
        }
      }
    })
  }
}
